import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import BusinessSearcherAutocomplete from 'lib/common/BusinessSearcherAutocomplete';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  createPendingBusinessAccount,
  editPendingBusinessAccount,
  getPendingAccountsCategories,
} from '../../../../../../../../lib/api';
import { getAllActiveResellsForFund } from '../../../../../../../../lib/api/reSell/factoring';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import {
  formatMoney,
  formatMoneyBack,
  showAlert,
} from '../../../../../../../../lib/functions/utils';

const ACCOUNT_TYPES = [
  { id: 'receivable', name: 'Por cobrar' },
  { id: 'payable', name: 'Por pagar' },
];

interface RelatedResell {
  id: number;
  folio: string;
  businessName: string;
  stakeholderName: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  fundName: string;
}

const NewAccount = (props: any) => {
  const { account, setAccount, setOpen, open, setUp } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingRelatedResells, setLoadingRelatedResells] = useState(false);
  const [business, setBusiness] = useState<any>(null);
  const [searched, setSearched] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [relatedResells, setRelatedResells] = useState<RelatedResell[]>([]);
  const [haveToSelectResell, setHaveToSelectResell] = useState(false);
  const [selectedResell, setSelectedResell] = useState<RelatedResell | null>(
    null,
  );

  const validateAttributes = () => {
    if (
      account.category === '' ||
      account.observation === '' ||
      account.type === '' ||
      !account.amount ||
      !account.createdAt
    ) {
      showAlert(dispatch, 'error', 'Debes completar todos los campos');
      return false;
    }
    if (haveToSelectResell && !selectedResell) {
      showAlert(dispatch, 'error', 'Debes seleccionar una venta a fondo');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateAttributes()) return;

    const payload = {
      amount: account.amount,
      createdAt: new Date(account.createdAt),
      categoryId: account.category,
      observation: account.observation,
      type: account.type,
      businessIdentifier: business.identifier,
      factoringResellingId: selectedResell?.id || undefined,
    };
    setLoading(true);
    try {
      if (account.id) {
        await editPendingBusinessAccount(dispatch, account.id, {
          ...payload,
        });
        showAlert(dispatch, 'success', 'Cuenta editada con éxito');
      } else {
        await createPendingBusinessAccount(dispatch, {
          ...payload,
        });
        showAlert(dispatch, 'success', 'Cuenta creada con éxito');
      }
      setOpen(false);
      await setUp();
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  const getRelatedResells = async () => {
    setLoadingRelatedResells(true);
    if (!business) {
      setRelatedResells([]);
      setHaveToSelectResell(true);
    } else {
      try {
        const response = await getAllActiveResellsForFund(
          dispatch,
          business.identifier,
        );
        setRelatedResells(response);
        setHaveToSelectResell(true);
      } catch (error) {
        showAlert(
          dispatch,
          'error',
          'Error al obtener las ventas a fondo relacionadas',
        );
      }
    }
    setLoadingRelatedResells(false);
  };

  const handleChangeCategory = (event: SelectChangeEvent<string>) => {
    const category = categories.find((x) => x.id === event.target.value);
    if (category.id === -1 || category.id === -2) {
      void getRelatedResells();
    } else {
      setHaveToSelectResell(false);
      setRelatedResells([]);
    }
    setAccount({
      ...account,
      category: event.target.value,
      income: category?.income,
    });
  };

  const categoriesSetUp = async () => {
    try {
      setLoading(true);
      const response = await getPendingAccountsCategories(dispatch);
      setCategories(response);
    } catch (error) {
      alert('Error al cargar las categorías');
    }
    setLoading(false);
  };

  useEffect(() => {
    void categoriesSetUp();
  }, []);

  const handleChangeResell = (
    event: React.SyntheticEvent,
    newValue: RelatedResell | null,
  ) => {
    setSelectedResell(newValue);
  };

  useEffect(() => {
    const categoryId = account.category;
    if (categoryId === -1 || categoryId === -2) {
      void getRelatedResells();
    } else {
      setHaveToSelectResell(false);
      setRelatedResells([]);
    }
  }, [business]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {' '}
        {account.id ? 'Editar cuenta' : 'Nueva cuenta'}{' '}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div>
            {!account.id && (
              <div>
                <BusinessSearcherAutocomplete
                  setBusiness={setBusiness}
                  setSearched={setSearched}
                />
              </div>
            )}
            {business?.id && searched && (
              <div>
                <Divider />
                {loadingRelatedResells ? (
                  <CenteredCircularProgress relative />
                ) : (
                  <div>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      margin="4px 0px"
                    >
                      {' '}
                      Datos de la cuenta{' '}
                    </Typography>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="filter-label">Tipo de cuenta</InputLabel>
                      <Select
                        labelId="filter-label"
                        id="Filtrar por estado"
                        fullWidth
                        value={account.type}
                        onChange={(e) =>
                          setAccount({ ...account, type: e.target.value })
                        }
                        label="Tipo de cuenta"
                      >
                        {ACCOUNT_TYPES.map((x: any, index: any) => (
                          <MenuItem key={index} value={x.id}>
                            {x.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="filter-label">Categoría</InputLabel>
                      <Select
                        labelId="filter-label"
                        id="Filtrar por estado"
                        fullWidth
                        value={account.category}
                        onChange={handleChangeCategory}
                        label="Categoría"
                      >
                        {categories.map((x: any, index: any) => (
                          <MenuItem key={index} value={x.id}>
                            {x.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      margin="4px 0px"
                    >
                      {categories.find((x) => x.id === account.category)
                        ?.income && 'Esta categoría se considera un ingreso'}
                    </Typography>
                    {haveToSelectResell && (
                      <Autocomplete
                        options={relatedResells}
                        getOptionLabel={(option: RelatedResell) =>
                          `${option.id} | ${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                        }
                        value={selectedResell}
                        onChange={handleChangeResell}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label="Buscar ventas a fondo por id, folio, cliente o deudor"
                            placeholder="Selecciona ventas a fondo..."
                          />
                        )}
                        filterSelectedOptions
                        fullWidth
                      />
                    )}
                    <TextField
                      margin="dense"
                      id="observation"
                      label="Observación"
                      type="text"
                      fullWidth
                      value={account.observation}
                      onChange={(e) =>
                        setAccount({ ...account, observation: e.target.value })
                      }
                    />
                    <TextField
                      margin="dense"
                      id="amount"
                      label="Monto"
                      fullWidth
                      value={formatMoney(account.amount)}
                      onChange={(e) =>
                        setAccount({
                          ...account,
                          amount: formatMoneyBack(e.target.value),
                        })
                      }
                    />
                    <TextField
                      margin="dense"
                      id="createdAt"
                      label="Fecha"
                      type="date"
                      fullWidth
                      value={account.createdAt}
                      onChange={(e) =>
                        setAccount({ ...account, createdAt: e.target.value })
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          {' '}
          Cancelar{' '}
        </CustomButton>
        <CustomButton
          disabled={(!business && !account.id) || loading}
          color="primary"
          onClick={() => handleSubmit()}
        >
          {' '}
          {account.id ? 'Guardar' : 'Crear'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewAccount;
